import type { PoiItem } from '@/types/map';
import type { RoutePoint } from '@/types/route-simulation';
import type { IBuilding } from 'dabeeomaps/dist/src/model/map/IBuilding';
import type { IDataFloor } from 'dabeeomaps/dist/src/model/map/IDataFloor';
import { isValidNumber } from './number';
import type { FloorInfo } from '@/services/dto/kiosk-config';

const qrHereUrl = import.meta.env.VITE_QR_HERE_SERVICE_URL;

export const collectCategoryPoisForFloorKoName = (
    floorKoName: string,
    groupedPoiByFloorId: Map<string, Map<string, PoiItem[]>>,
    categoriesCode: string[]
): PoiItem[] => {
    return categoriesCode.flatMap((categoryCode) => {
        const poi = groupedPoiByFloorId.get(floorKoName)?.get(categoryCode);
        return poi ?? [];
    });
};

const resolveStoreFloorForPois = (
    pois: PoiItem[],
    preferredFloors: FloorInfo[],
    allStoreFloors: FloorInfo[]
): FloorInfo | null => {
    if (!pois.length) {
        return null;
    }

    const poiFloorIds = new Set(pois.map((poi) => poi.floorId).filter((id): id is string => Boolean(id)));

    const fromPreferred = preferredFloors.find((floor) => poiFloorIds.has(floor.floor_code));
    if (fromPreferred) {
        return fromPreferred;
    }

    return allStoreFloors.find((floor) => poiFloorIds.has(floor.floor_code)) ?? null;
};

const resolveFloorWithCategoryPoi = (
    floorKoName: string,
    preferredFloors: FloorInfo[],
    groupedPoiByFloorId: Map<string, Map<string, PoiItem[]>>,
    categoriesCode: string[],
    allStoreFloors: FloorInfo[]
): FloorInfo | null => {
    const pois = collectCategoryPoisForFloorKoName(floorKoName, groupedPoiByFloorId, categoriesCode);
    if (!pois.length) {
        return null;
    }

    const storeFloors = allStoreFloors.length ? allStoreFloors : preferredFloors;
    return resolveStoreFloorForPois(pois, preferredFloors, storeFloors);
};

export const findNearestFloorWithPoi = (
    sortedFloors: FloorInfo[],
    name: string,
    groupedPoiByFloorId: Map<string, Map<string, PoiItem[]>>,
    categoriesCode: string[],
    allStoreFloors: FloorInfo[] = []
): FloorInfo | null => {
    if (!Array.isArray(sortedFloors)) {
        return null;
    }
    const floorIdx = sortedFloors.findIndex((floor) => floor?.name['ko'] === name);

    if (floorIdx === -1) {
        return null;
    }

    const currentFloor = sortedFloors[floorIdx];
    const currentResolved = resolveFloorWithCategoryPoi(
        name,
        currentFloor ? [currentFloor] : [],
        groupedPoiByFloorId,
        categoriesCode,
        allStoreFloors
    );
    if (currentResolved) {
        return currentResolved;
    }

    let left = floorIdx - 1;
    let right = floorIdx + 1;

    while (left >= 0 || right < sortedFloors.length) {
        if (left >= 0) {
            const neighborFloor = sortedFloors[left];
            const neighborResolved = resolveFloorWithCategoryPoi(
                neighborFloor?.name['ko'] ?? '',
                neighborFloor ? [neighborFloor] : [],
                groupedPoiByFloorId,
                categoriesCode,
                allStoreFloors
            );
            if (neighborResolved) {
                return neighborResolved;
            }
        }
        if (right < sortedFloors.length) {
            const neighborFloor = sortedFloors[right];
            const neighborResolved = resolveFloorWithCategoryPoi(
                neighborFloor?.name['ko'] ?? '',
                neighborFloor ? [neighborFloor] : [],
                groupedPoiByFloorId,
                categoriesCode,
                allStoreFloors
            );
            if (neighborResolved) {
                return neighborResolved;
            }
        }
        left--;
        right++;
    }

    return null;
};

export const generateQrHereUrl = (serviceId: string, startPoint: RoutePoint, PoiIdEndPoint: string) => {
    if (!qrHereUrl || !startPoint?.position?.x || !startPoint?.position?.y || !startPoint?.floorId || !PoiIdEndPoint) {
        return '';
    }
    return `${qrHereUrl}/${serviceId}?${startPoint?.position?.x},${startPoint?.position?.y},FL-${startPoint.floorId},PO-${PoiIdEndPoint}`;
};

export const getAllFloorsFromIndoorBuildings = (buildings?: IBuilding[]): IDataFloor[] => {
    if (!Array.isArray(buildings) || !buildings?.length) {
        return [];
    }

    const indoorBuildings = buildings.filter((building) => building.buildingType === 'indoor');

    if (!indoorBuildings.length) {
        return [];
    }

    const allFloors: IDataFloor[] = [];
    indoorBuildings.forEach((building) => {
        building.floors.forEach((floor) => {
            allFloors.push(floor);
        });
    });

    return allFloors;
};

export const getUniqueFloorsByKoreanName = (floors: IDataFloor[]): IDataFloor[] => {
    if (!Array.isArray(floors) || !floors?.length) {
        return [];
    }

    const floorMap = new Map<string, number>();
    const uniqueFloors: IDataFloor[] = [];

    floors.forEach((floor) => {
        const koName = floor.name.find((n) => n.lang === 'ko');
        if (koName && !floorMap.has(koName.text)) {
            floorMap.set(koName.text, floor.order);
            uniqueFloors.push(floor);
        }
    });

    return uniqueFloors;
};

export const extractUniqueFloorsFromBuildings = (buildings?: IBuilding[]): IDataFloor[] => {
    const allFloors = getAllFloorsFromIndoorBuildings(buildings);
    return getUniqueFloorsByKoreanName(allFloors);
};

export const sortFloorsByOrder = (floors: IDataFloor[], direction: 'asc' | 'desc'): IDataFloor[] => {
    if (!Array.isArray(floors) || !floors?.length) {
        return [];
    }

    return [...floors].sort((a, b) => (direction === 'asc' ? a.order - b.order : b.order - a.order));
};

export const degreeToRadian = (degree?: number) => {
    return isValidNumber(degree) ? degree * (Math.PI / 180) : 0;
};

export const getDirectionVectors = (theta: number) => {
    const angle = -theta;

    const cosT = Math.cos(angle);
    const sinT = Math.sin(angle);

    const up = { x: sinT, y: -cosT };

    const right = { x: cosT, y: sinT };

    return {
        up,
        down: { x: -up.x, y: -up.y },
        left: { x: -right.x, y: -right.y },
        right,
    };
};

export const getPoiObjectIds = (poiList?: PoiItem[]): string[] => {
    if (!Array.isArray(poiList) || !poiList?.length) {
        return [];
    }

    const objectIds = poiList.map((item) => item?.objectId);
    return objectIds.filter((item) => item !== undefined);
};

/**
 * Helper function: Đợi map render xong trước khi thực hiện moveTo
 * Sử dụng requestAnimationFrame để đảm bảo map đã vẽ xong frame hiện tại
 * @param frames - Số frames cần đợi (mặc định là 3 frames để đảm bảo)
 */
export const waitForMapRender = async (frames: number = 3): Promise<void> => {
    return new Promise<void>((resolve) => {
        let frameCount = 0;

        const waitFrame = () => {
            frameCount++;
            if (frameCount >= frames) {
                resolve();
            } else {
                requestAnimationFrame(waitFrame);
            }
        };

        requestAnimationFrame(waitFrame);
    });
};

/**
 * Helper function nâng cao: Đợi và verify marker đã được vẽ trên map
 * Sử dụng hybrid approach: RAF + polling + timeout để đảm bảo marker thực sự được render
 * @param currentMap - DabeeoMap instance
 * @param markerId - ID của marker cần verify
 * @param position - Vị trí marker {x, y} để verify
 * @param maxRetries - Số lần retry tối đa (mặc định 10)
 * @param debugContext - Context để debug log
 */
export const waitForMarkerReady = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentMap: any,
    markerId?: string,
    _position?: { x?: number; y?: number },
    maxRetries: number = 10,
    debugContext?: string
): Promise<boolean> => {
    if (!currentMap || !markerId) {
        // eslint-disable-next-line no-console
        console.warn(`[${debugContext}] Invalid params for waitForMarkerReady`);
        return false;
    }

    // Tier 1: Đợi 3 frames trước (đảm bảo browser đã paint)
    await waitForMapRender(3);

    // Tier 2: Polling để verify marker thực sự tồn tại
    let retryCount = 0;
    const retryDelays = [50, 50, 100, 100, 150, 200, 250, 300]; // Progressive backoff

    while (retryCount < maxRetries) {
        try {
            // Kiểm tra marker có tồn tại không (nếu map API hỗ trợ)
            // Một số map engine có method getMarker hoặc markers.get
            const markerExists = currentMap?.markers?.get?.(markerId);

            if (markerExists) {
                if (debugContext) {
                    // eslint-disable-next-line no-console
                    console.log(`✅ [${debugContext}] Marker ready after ${retryCount} retries`);
                }
                return true;
            }

            // Nếu không có method get, chờ một thời gian hợp lý
            const delay = retryDelays[retryCount] || 300;
            await new Promise((resolve) => setTimeout(resolve, delay));

            retryCount++;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn(`[${debugContext}] Error checking marker:`, error);
            retryCount++;
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
    }

    if (debugContext) {
        // eslint-disable-next-line no-console
        console.warn(`⚠️ [${debugContext}] Marker not verified after ${maxRetries} retries, proceeding anyway`);
    }

    // Return true anyway để không block flow, nhưng log warning
    return true;
};

/**
 * Helper function: Safe moveTo với retry logic
 * Đảm bảo moveTo được thực thi thành công, retry nếu fail
 * @param currentMap - DabeeoMap instance
 * @param position - Position to move to
 * @param floorId - Floor ID
 * @param transition - Enable transition animation
 * @param maxRetries - Số lần retry tối đa (mặc định 3)
 * @param debugContext - Context để debug
 */
export const safeMoveToMarker = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentMap: any,
    position: { x: number; y: number },
    floorId?: string,
    transition: boolean = true,
    maxRetries: number = 3,
    debugContext?: string
): Promise<boolean> => {
    if (!currentMap?.control?.moveTo) {
        // eslint-disable-next-line no-console
        console.warn(`[${debugContext}] Map control not available`);
        return false;
    }

    if (!isValidNumber(position?.x) || !isValidNumber(position?.y)) {
        // eslint-disable-next-line no-console
        console.warn(`[${debugContext}] Invalid position:`, position);
        return false;
    }

    let retryCount = 0;

    while (retryCount < maxRetries) {
        try {
            if (debugContext && retryCount > 0) {
                // eslint-disable-next-line no-console
                console.log(`[${debugContext}] Retry moveTo attempt ${retryCount + 1}/${maxRetries}`);
            }

            currentMap.control.moveTo({
                position: { x: position.x, y: position.y },
                floorId,
                transition,
            });

            // Đợi một frame để verify
            await waitForMapRender(1);

            // Get current camera position để verify moveTo đã work
            const currentCamera = currentMap?.control?.getCurrentCameraInfo?.();
            if (currentCamera?.position) {
                const distance = Math.sqrt(Math.pow(currentCamera.position.x - position.x, 2) + Math.pow(currentCamera.position.y - position.y, 2));

                // Nếu khoảng cách nhỏ hơn threshold, coi như thành công
                const threshold = 1000; // Điều chỉnh tùy map scale
                if (distance < threshold || retryCount === maxRetries - 1) {
                    if (debugContext) {
                        // eslint-disable-next-line no-console
                        console.log(`✅ [${debugContext}] MoveTo successful (distance: ${distance.toFixed(2)})`);
                    }
                    return true;
                }
            }

            // Nếu chưa thành công, đợi thêm
            await new Promise((resolve) => setTimeout(resolve, 100));
            retryCount++;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(`[${debugContext}] MoveTo error:`, error);
            retryCount++;
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
    }

    if (debugContext) {
        // eslint-disable-next-line no-console
        console.warn(`⚠️ [${debugContext}] MoveTo completed after ${maxRetries} attempts`);
    }

    return true;
};
